import { API } from './site';

// AI audio only. Never silently fall back to a device/robotic speech voice.
export class NaturalVoicePlayer {
  constructor(callbacks) {
    this.callbacks = callbacks;
    this.audio = new Audio();
    this.audio.preload = 'auto';
    this.audio.setAttribute('playsinline', '');
    this.queue = [];
    this.inflight = new Set();
    this.epoch = 0;
    this.active = false;
    this.current = null;
    this.delivered = '';
  }

  start() { this.active = true; this.unlock(); }

  unlock() {
    if (this.current) return;
    // A brief, silent PCM buffer unlocks this same media element during the visitor's click.
    // This is not a generated reply and is never used as a speech fallback.
    if (!this.silentUrl) {
      const bytes = new ArrayBuffer(1644); const view = new DataView(bytes);
      const write = (offset, value) => [...value].forEach((letter, i) => view.setUint8(offset + i, letter.charCodeAt(0)));
      write(0, 'RIFF'); view.setUint32(4, 1636, true); write(8, 'WAVE'); write(12, 'fmt ');
      view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
      view.setUint32(24, 8000, true); view.setUint32(28, 16000, true); view.setUint16(32, 2, true);
      view.setUint16(34, 16, true); write(36, 'data'); view.setUint32(40, 1600, true);
      this.silentUrl = URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
    }
    const epoch = this.epoch;
    this.audio.src = this.silentUrl;
    this.audio.play().then(() => {
      if (epoch === this.epoch && !this.current) { this.audio.pause(); this.audio.currentTime = 0; }
    }).catch(() => {}); // Actual playback reports permission errors with an actionable message.
  }

  enqueue(text) {
    if (!this.active || !text.trim()) return;
    this.queue.push({ text, callId: this.callbacks.getCallId(), epoch: this.epoch, state: 'queued', controller: new AbortController() });
    this.pumpRequests(); this.pumpPlayback();
  }

  pumpRequests() {
    if (!this.active) return;
    while (this.inflight.size < 2) {
      const job = this.queue.find(item => item.state === 'queued');
      if (!job) break;
      job.state = 'loading'; this.inflight.add(job);
      this.loadClip(job);
    }
  }

  async loadClip(job) {
    const timer = setTimeout(() => job.controller.abort(), 45000);
    try {
      const response = await fetch(`${API}/voice`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_id: job.callId, text: job.text }), signal: job.controller.signal });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(typeof error.detail === 'string' ? error.detail : 'The natural voice is temporarily unavailable.');
      }
      const result = await response.json();
      if (!/^\/api\/voice\/[a-f0-9-]+\.mp3$/.test(result.audio_path)) throw new Error('The speech response could not be loaded.');
      if (job.epoch !== this.epoch || !this.active) return;
      const clipUrl = `${process.env.REACT_APP_BACKEND_URL}${result.audio_path}`;
      if (this.queue[0] === job && !this.current) {
        // Head of the queue: play straight from the streaming endpoint so Jenny starts
        // speaking as soon as the first audio chunk arrives, not once the clip is complete.
        job.url = clipUrl; job.streaming = true; job.state = 'ready';
      } else {
        // Lookahead clips are buffered fully so they follow on back-to-back without gaps.
        const audioResponse = await fetch(clipUrl, { signal: job.controller.signal });
        if (!audioResponse.ok) throw new Error('The speech clip is unavailable. Please ask again.');
        const blob = await audioResponse.blob();
        if (job.epoch !== this.epoch || !this.active) return;
        if (!blob.size || !blob.type.startsWith('audio/')) throw new Error('The voice service returned an invalid audio clip.');
        job.url = URL.createObjectURL(blob); job.state = 'ready';
      }
    } catch (error) {
      if (job.epoch !== this.epoch || !this.active) return;
      job.state = 'error';
      job.error = error.name === 'AbortError' ? 'The natural voice took too long to load. Please continue using the transcript.' : error.message;
    } finally {
      clearTimeout(timer); this.inflight.delete(job);
      this.pumpRequests(); this.pumpPlayback();
    }
  }

  pumpPlayback() {
    if (!this.active || this.current) return;
    const job = this.queue[0];
    if (!job) { this.callbacks.onPreparing(false); return; }
    if (job.state === 'error') { this.fail(job.error); return; }
    if (job.state !== 'ready') { this.callbacks.onPreparing(true); return; }
    this.current = job;
    const current = () => this.active && job.epoch === this.epoch && this.current === job;
    this.audio.onplaying = () => {
      if (!current()) return;
      this.callbacks.onPreparing(false); this.callbacks.onSpeaking(true);
    };
    this.audio.ontimeupdate = () => {
      if (!current() || !Number.isFinite(this.audio.duration) || !this.audio.duration) return;
      const ratio = Math.min(1, this.audio.currentTime / this.audio.duration);
      let heard = job.text.slice(0, Math.floor(job.text.length * ratio));
      if (ratio < 0.98) heard = heard.slice(0, Math.max(0, heard.lastIndexOf(' ')));
      else heard = job.text;
      this.callbacks.onProgress(`${this.delivered} ${heard}`.trim());
    };
    this.audio.onended = () => {
      if (!current()) return;
      this.delivered = `${this.delivered} ${job.text}`.trim();
      this.callbacks.onProgress(this.delivered);
      this.releaseMedia(); this.current = null; this.queue.shift();
      this.callbacks.onSpeaking(false);
      this.pumpRequests(); this.pumpPlayback();
    };
    this.audio.onerror = () => { if (current()) this.fail('The natural voice could not play. Your reply is available in the transcript.'); };
    this.audio.src = job.url;
    this.audio.play().catch(error => {
      if (!current()) return;
      this.fail(error.name === 'NotAllowedError'
        ? 'Your browser blocked audio. Select “Enable natural voice”, then ask your question again.'
        : 'The natural voice could not start. You can continue using the transcript.');
    });
    clearTimeout(this.playTimer);
    this.playTimer = setTimeout(() => { if (current()) this.fail('Voice playback paused. Please ask again or use the transcript.'); }, Math.max(30000, job.text.length * 160));
  }

  releaseMedia() {
    clearTimeout(this.playTimer);
    this.audio.onplaying = null; this.audio.onended = null; this.audio.onerror = null; this.audio.ontimeupdate = null;
    this.audio.pause(); this.audio.removeAttribute('src'); this.audio.load();
    if (this.current?.url) { URL.revokeObjectURL(this.current.url); this.current.url = null; }
  }

  fail(message) { this.cancel(); this.callbacks.onError(message); }

  cancel() {
    this.epoch += 1;
    this.releaseMedia(); this.current = null;
    for (const job of this.queue) { job.controller.abort(); if (job.url) URL.revokeObjectURL(job.url); }
    this.queue = []; this.delivered = '';
    this.callbacks.onPreparing(false); this.callbacks.onSpeaking(false);
  }

  stop() {
    this.active = false; this.cancel();
    if (this.silentUrl) { URL.revokeObjectURL(this.silentUrl); this.silentUrl = null; }
  }
}